import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Inbox, Loader2, Plus, Trash2 } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/shared/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { usePermissions } from '@/shared/hooks/usePermissions'
import { InstallationDeleteConfirmDialog } from '@/features/installations/components/installation-delete-confirm-dialog'
import {
  useCbamGoodsCodeLookupQuery,
  useInstallationProducedCnCodeMutations,
  useInstallationProducedCnCodesQuery,
} from '@/features/installations/hooks/useInstallationConfiguration'
import { useInstallationDeleteConfirm } from '@/features/installations/hooks/use-installation-delete-confirm'

const CN_DIGITS_PATTERN = /^\d{8}$/

export function InstallationProducedCnCodesPage() {
  const { t } = useTranslation('installations')
  const { checkPermission } = usePermissions()
  const canEdit = checkPermission('installation', 'update')

  const listQuery = useInstallationProducedCnCodesQuery()
  const mutations = useInstallationProducedCnCodeMutations()
  const deleteConfirm = useInstallationDeleteConfirm()

  const [createOpen, setCreateOpen] = useState(false)
  const [producedInput, setProducedInput] = useState('')

  const rows = listQuery.data

  const trimmedProducedInput = producedInput.trim()
  const hasValidProducedInput = CN_DIGITS_PATTERN.test(trimmedProducedInput)
  const isProducedAlreadyAdded =
    hasValidProducedInput &&
    Boolean(rows?.some((row) => row.cnCode === trimmedProducedInput))

  const cbamRelevanceQuery = useCbamGoodsCodeLookupQuery({
    goodsCode8Digits: producedInput,
    enabled: createOpen && hasValidProducedInput && !isProducedAlreadyAdded,
  })

  const resetCreateForm = () => {
    setProducedInput('')
  }

  const submitProduced = async () => {
    const value = producedInput.trim()
    if (!CN_DIGITS_PATTERN.test(value)) {
      return
    }
    if (rows?.some((row) => row.cnCode === value)) {
      return
    }
    await mutations.addProducedCnCode.mutateAsync(value)
    resetCreateForm()
    setCreateOpen(false)
  }

  const handleConfirmDelete = async () => {
    if (!deleteConfirm.pendingDelete) {
      return
    }
    await mutations.removeProducedCnCode.mutateAsync(deleteConfirm.pendingDelete.id)
    deleteConfirm.clearPendingDelete()
  }

  if (listQuery.isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center p-6">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (rows === undefined) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-6">
        <p className="text-muted-foreground">{t('configuration.loadError')}</p>
      </div>
    )
  }

  const showCnCodeFeedback = createOpen && hasValidProducedInput

  return (
    <div className="flex flex-1 flex-col gap-6 p-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t('configuration.producedCn.title')}
          </h1>
          <p className="text-muted-foreground">{t('configuration.producedCn.description')}</p>
        </div>
        {canEdit && rows.length > 0 && (
          <Button type="button" onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" />
            {t('common:actions.create')}
          </Button>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-4 rounded-md border border-dashed py-16">
          <Inbox className="size-10 text-muted-foreground/50" />
          <div className="space-y-1 text-center">
            <h3 className="text-lg font-semibold">{t('configuration.producedCn.emptyTitle')}</h3>
            <p className="max-w-md text-sm text-muted-foreground">
              {t('configuration.producedCn.emptyDescription')}
            </p>
          </div>
          {canEdit && (
            <Button type="button" onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" />
              {t('common:actions.create')}
            </Button>
          )}
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border">
          <Table className="table-fixed">
            <TableHeader>
              <TableRow className="group/row">
                <TableHead
                  className={cn(
                    'bg-background group-hover/row:bg-muted',
                    'min-w-0 max-w-[55%] whitespace-normal'
                  )}
                >
                  {t('configuration.table.cnCode')}
                </TableHead>
                <TableHead
                  className={cn(
                    'bg-background group-hover/row:bg-muted',
                    'w-[min(32%,200px)] min-w-0 max-w-[200px] whitespace-normal'
                  )}
                >
                  {t('configuration.table.cbamRelevance')}
                </TableHead>
                {canEdit && (
                  <TableHead
                    className={cn(
                      'bg-background group-hover/row:bg-muted',
                      'w-[88px] min-w-0 max-w-[88px] text-end'
                    )}
                  >
                    {t('configuration.table.actions')}
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} className="group/row">
                  <TableCell
                    className={cn(
                      'bg-background group-hover/row:bg-muted',
                      'min-w-0 max-w-[55%] whitespace-normal font-mono'
                    )}
                  >
                    {row.cnCode}
                  </TableCell>
                  <TableCell
                    className={cn(
                      'bg-background group-hover/row:bg-muted',
                      'w-[min(32%,200px)] min-w-0 max-w-[200px]'
                    )}
                  >
                    <Badge variant={row.cbamRelevant ? 'default' : 'secondary'}>
                      {row.cbamRelevant
                        ? t('configuration.producedCn.cbamBadgeYes')
                        : t('configuration.producedCn.cbamBadgeNo')}
                    </Badge>
                  </TableCell>
                  {canEdit && (
                    <TableCell
                      className={cn(
                        'bg-background group-hover/row:bg-muted',
                        'w-[88px] min-w-0 max-w-[88px] text-end'
                      )}
                    >
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="text-destructive"
                        aria-label={t('configuration.a11y.deleteProducedCn', {
                          cnCode: row.cnCode,
                        })}
                        onClick={() =>
                          deleteConfirm.requestDelete({
                            id: row.id,
                            label: row.cnCode,
                          })
                        }
                        disabled={mutations.removeProducedCnCode.isPending}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open)
          if (!open) {
            resetCreateForm()
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('configuration.dialog.addProducedCn')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <Input
              inputMode="numeric"
              maxLength={8}
              placeholder={t('configuration.cnPlaceholder')}
              value={producedInput}
              onChange={(event) => setProducedInput(event.target.value.replace(/\D/g, ''))}
            />
            {showCnCodeFeedback && isProducedAlreadyAdded && (
              <Alert>
                <AlertDescription>{t('configuration.cnCodeAlreadyAdded')}</AlertDescription>
              </Alert>
            )}
            {showCnCodeFeedback && !isProducedAlreadyAdded && (
              <div className="space-y-2">
                {cbamRelevanceQuery.isFetching && (
                  <p className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="size-4 shrink-0 animate-spin" />
                    {t('configuration.producedCn.cbamRelevanceChecking')}
                  </p>
                )}
                {cbamRelevanceQuery.isError && (
                  <p className="text-sm text-destructive">{t('configuration.loadError')}</p>
                )}
                {cbamRelevanceQuery.isSuccess && cbamRelevanceQuery.data.cbamRelevant && (
                  <Alert variant="success">
                    <AlertDescription>
                      {t('configuration.producedCn.cbamRelevant')}
                    </AlertDescription>
                  </Alert>
                )}
                {cbamRelevanceQuery.isSuccess && !cbamRelevanceQuery.data.cbamRelevant && (
                  <Alert>
                    <AlertDescription>
                      {t('configuration.producedCn.cbamNotRelevant')}
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setCreateOpen(false)
                resetCreateForm()
              }}
            >
              {t('configuration.cancel')}
            </Button>
            <Button
              type="button"
              disabled={
                !hasValidProducedInput ||
                isProducedAlreadyAdded ||
                mutations.addProducedCnCode.isPending
              }
              onClick={submitProduced}
            >
              {t('common:actions.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <InstallationDeleteConfirmDialog
        pendingDelete={deleteConfirm.pendingDelete}
        open={deleteConfirm.isConfirmOpen}
        onOpenChange={(open) => {
          if (!open) {
            deleteConfirm.clearPendingDelete()
          }
        }}
        onConfirm={handleConfirmDelete}
        isLoading={mutations.removeProducedCnCode.isPending}
      />
    </div>
  )
}
